import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import type { OnboardCompanyRequest } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    companyName: z.string().min(1, t('form.validation.companyNameRequired')),
    cin: z.string().nullable(),
    gstin: z.string().nullable(),
    email: z.string().nullable(),
    industrySector: z.string().nullable(),
    currency: z.string().min(1),
    taxSystem: z.string().min(1),
    branchCode: z.string().min(1, t('form.validation.branchCodeRequired')),
    branchName: z.string().min(1, t('form.validation.branchNameRequired')),
    branchAddress: z.string().nullable(),
    adminUsername: z.string().min(1, t('form.validation.adminUsernameRequired')),
    adminEmail: z.string().min(1, t('form.validation.adminEmailRequired')),
    adminPassword: z.string().min(6, t('form.validation.adminPasswordMinLength')),
    adminFirstName: z.string().min(1, t('form.validation.adminFirstNameRequired')),
    adminLastName: z.string().min(1, t('form.validation.adminLastNameRequired')),
  });
}

export type OnboardFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface OnboardCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: OnboardCompanyRequest) => void;
  loading?: boolean;
  errorMessage?: string | null;
}

const DEFAULT_VALUES: OnboardFormValues = {
  companyName: '',
  cin: null,
  gstin: null,
  email: null,
  industrySector: null,
  currency: 'INR',
  taxSystem: 'GST',
  branchCode: '',
  branchName: '',
  branchAddress: null,
  adminUsername: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
};

export default function OnboardCompanyDialog({ open, onClose, onSubmit, loading, errorMessage }: OnboardCompanyDialogProps) {
  const { t } = useTranslation('companiesConsole');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<OnboardFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open) reset(DEFAULT_VALUES);
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('onboard.title')}</DialogTitle>
      <DialogContent>
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : undefined}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('onboard.sections.company')}
        </Typography>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={2}>
            <Controller
              name="companyName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} value={field.value ?? ''} label={t('form.companyName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="cin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.cin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="gstin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.gstin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="email" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.email')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="industrySector" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.industrySector')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="currency" control={control} render={({ field }) => <TextField {...field} label={t('form.currency')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="taxSystem" control={control} render={({ field }) => <TextField {...field} label={t('form.taxSystem')} fullWidth />} />
          </GridItem>
        </GridContainer>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('onboard.sections.branch')}
        </Typography>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="branchCode"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.branchCode')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="branchName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.branchName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller name="branchAddress" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.branchAddress')} fullWidth />} />
          </GridItem>
        </GridContainer>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('onboard.sections.admin')}
        </Typography>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="adminUsername"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.adminUsername')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="adminEmail"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.adminEmail')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="adminFirstName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.adminFirstName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="adminLastName"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.adminLastName')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="adminPassword"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="password"
                  label={t('form.adminPassword')}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </GridItem>
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('onboard.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
